import { Chess, type Move as ChessJsMove, type Square } from 'chess.js';
import type { Fen, GameResult, Move, PieceChar, PositionSnapshot, San, Turn } from '@strategy4kids/protocol';
import { START_FEN } from '@strategy4kids/protocol';
import type { AttackTable } from './attack-table.js';
import { buildAttackTable, controlScoreAt, squaresAttacked } from './attack-table.js';
import { parsePlacement, squareToIndex, type Placement } from './coords.js';
import type { GameAdapter, MoveTarget } from './game-adapter.js';
import type { AttackGen } from './shields.js';

export class IllegalMoveError extends Error {
  constructor(square: string) {
    super(`Illegal move: ${square}`);
    this.name = 'IllegalMoveError';
  }
}

function toMove(m: ChessJsMove): Move {
  return {
    from: m.from,
    to: m.to,
    san: m.san,
    color: m.color,
    piece: m.piece,
    captured: m.captured as PieceChar | undefined,
    promotion: m.promotion as PieceChar | undefined,
    flags: m.flags,
  };
}

function resultOf(chess: Chess): GameResult | null {
  if (chess.isCheckmate()) {
    return { kind: 'checkmate', winner: chess.turn() === 'w' ? 'b' : 'w' };
  }
  if (chess.isStalemate()) return { kind: 'stalemate' };
  if (chess.isInsufficientMaterial()) return { kind: 'insufficient' };
  if (chess.isThreefoldRepetition()) return { kind: 'threefold' };
  if (chess.isDraw()) return { kind: 'draw' };
  return null;
}

function capturedOf(chess: Chess): { w: PieceChar[]; b: PieceChar[] } {
  const w: PieceChar[] = [];
  const b: PieceChar[] = [];
  for (const m of chess.history({ verbose: true })) {
    if (m.captured) {
      const list = m.color === 'w' ? w : b;
      list.push(m.captured as PieceChar);
    }
  }
  return { w, b };
}

function snapshotFromChess(chess: Chess, captured: { w: PieceChar[]; b: PieceChar[] } = capturedOf(chess)): PositionSnapshot {
  return {
    fen: chess.fen(),
    turn: chess.turn(),
    legalMoves: chess.moves({ verbose: false }),
    result: resultOf(chess),
    captured,
  };
}

function materialOf(placement: Placement): number {
  let score = 0;
  for (let i = 0; i < 64; i++) {
    const char = placement[i];
    if (char == null) continue;
    const type = char.toLowerCase();
    const value = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 }[type] ?? 0;
    score += char === char.toUpperCase() ? value : -value;
  }
  return score;
}

export class ChessAdapter implements GameAdapter {
  readonly gameId = 'chess';

  /** Full move list keyed by resulting FEN; recorded by makeMove (a Chess built from FEN has no history). */
  private readonly historyByFen = new Map<string, Move[]>();

  newGame(): PositionSnapshot {
    return this.fromFen(START_FEN);
  }

  fromFen(fen: Fen): PositionSnapshot {
    this.historyByFen.clear();
    return snapshotFromChess(new Chess(fen));
  }

  legalMoves(pos: PositionSnapshot): San[] {
    return pos.legalMoves;
  }

  legalMovesForSquare(pos: PositionSnapshot, from: string): MoveTarget[] {
    const chess = new Chess(pos.fen);
    return chess
      .moves({ square: from as Square, verbose: true })
      .map((m) => ({ to: m.to, capture: Boolean(m.captured) || m.flags.includes('e') }));
  }

  makeMove(pos: PositionSnapshot, from: string, to: string): PositionSnapshot {
    const chess = new Chess(pos.fen);
    let move: ChessJsMove;
    try {
      move = chess.move({ from: from as Square, to: to as Square, promotion: 'q' });
    } catch {
      throw new IllegalMoveError(`${from}-${to}`);
    }
    // Reconstruct the full move list: this position's prior history (recorded the
    // last time this FEN was reached) plus the move just played. A Chess built from
    // FEN carries no history, so chess.history() alone would drop every prior ply.
    const prior = this.historyByFen.get(pos.fen) ?? [];
    const moves = [...prior, toMove(move)];
    const captured: { w: PieceChar[]; b: PieceChar[] } = {
      w: move.color === 'w' && move.captured ? [...pos.captured.w, move.captured as PieceChar] : [...pos.captured.w],
      b: move.color === 'b' && move.captured ? [...pos.captured.b, move.captured as PieceChar] : [...pos.captured.b],
    };
    this.historyByFen.set(chess.fen(), moves);
    return snapshotFromChess(chess, captured);
  }

  result(pos: PositionSnapshot): GameResult | null {
    return pos.result;
  }

  evaluate(pos: PositionSnapshot): number {
    const placement = parsePlacement(pos.fen);
    let control = 0;
    for (let i = 0; i < 64; i++) control += controlScoreAt(placement, i);
    return materialOf(placement) + 0.05 * control;
  }

  attackTable(pos: PositionSnapshot): AttackTable {
    return buildAttackTable(parsePlacement(pos.fen));
  }

  controlScore(pos: PositionSnapshot, sq: string): number {
    return controlScoreAt(parsePlacement(pos.fen), squareToIndex(sq));
  }

  attackGen(): AttackGen {
    return squaresAttacked;
  }

  history(pos: PositionSnapshot): Move[] {
    return this.historyByFen.get(pos.fen) ?? [];
  }

  pieceAt(pos: PositionSnapshot, sq: string): { char: 'p' | 'n' | 'b' | 'r' | 'q' | 'k'; color: Turn } | null {
    const chess = new Chess(pos.fen);
    const p = chess.get(sq as Square);
    if (!p) return null;
    return { char: p.type, color: p.color };
  }

  isCheck(pos: PositionSnapshot): boolean {
    return new Chess(pos.fen).inCheck();
  }

  kingSquare(pos: PositionSnapshot, color: Turn): string | null {
    const chess = new Chess(pos.fen);
    const board = chess.board();
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        const p = board[r]?.[c];
        if (p && p.type === 'k' && p.color === color) {
          return `${String.fromCharCode(97 + c)}${8 - r}`;
        }
      }
    }
    return null;
  }
}

export { parsePlacement };
export type { Placement };
